// Diğer Varlıklar (Task 15 + Task 16) — Termometre dışında tutulan kategoriler
// + büyük resim toplam. BirikimDağılımı'nın altında konumlanır.
//
// Task 16 ile politika netleşti:
//   - BES (emeklilik): tabu (her zaman ayrı)
//   - Tekne: kullanıcı kararıyla `include_in_termometer=0` (Migration 0008)
//   - Properties (Işık vb.): henüz satılmamışsa LİKİT DEĞİL → ayrı; sold olunca
//     `estimated_value=0` döner ve nakit `combined.total_value_tl`'ye akar.
//
// Termometre payı (`total_with_termometer_assets_tl`) BirikimDağılımı'nda;
// burası "büyük resim ne kadar?" sorusunu yanıtlar.

import { fmtTLCompact } from "../lib/format";
import type { PortfolioCombined } from "../types/api";

interface Props {
  combined: PortfolioCombined | null;
}

function safeNum(n: number | null | undefined): number {
  return n === null || n === undefined || !Number.isFinite(n) ? 0 : n;
}

// "Ayrı tutulan" varlık satırı — properties (Işık) + other_assets (BES vb.)
// ortak şema (her ikisi aynı tipte gösterilir).
interface AyriTutulanRow {
  key: string;
  name: string;
  /** "Gayrimenkul" / "Emeklilik" / "Araç" / "Diğer". */
  kind_label: string;
  /** "Gökhan" / "Eş" / "Anne" — owner != self ise gösterilir. */
  owner_label?: string;
  value_tl: number;
  is_stale: boolean;
  /** Tooltip mesajı — neden burada? */
  reason: string;
}

export default function DigerVarliklar({ combined }: Props) {
  if (!combined) return null;

  const others = combined.other_assets?.items ?? [];
  const properties = combined.properties?.items ?? [];
  const propertyAnyStale = combined.properties?.any_stale ?? false;
  const totalAssets = safeNum(combined.combined?.total_assets_tl);
  const termometerToplam = safeNum(
    combined.combined?.total_with_termometer_assets_tl ??
      combined.combined?.total_value_tl,
  );

  const ownerLabel: Record<string, string> = {
    self: "Gökhan",
    spouse: "Eş",
    mother: "Anne",
  };
  const kindLabel: Record<string, string> = {
    pension: "Emeklilik",
    vehicle: "Araç",
    other: "Diğer",
  };

  const rows: AyriTutulanRow[] = [];

  // Task 16 — Properties (Işık vb.): satılmamış olduğu sürece Termometre dışı.
  for (const p of properties) {
    if (safeNum(p.estimated_value_tl) <= 0) continue;
    rows.push({
      key: `prop:${p.id}`,
      name: p.name,
      kind_label: "Gayrimenkul",
      value_tl: safeNum(p.estimated_value_tl),
      is_stale: !!p.is_stale,
      reason:
        "Henüz satılmadı — likit değil. Satılınca nakit fon portföyüne akar ve Termometre'ye otomatik girer.",
    });
  }

  // BES + Termometre flag'i 0 olan other_assets.
  for (const a of others) {
    if (a.include_in_termometer) continue;
    const reason =
      a.kind === "pension"
        ? "Emeklilik birikimi — erken çıkış cezası + stopaj olur, T=0 bütçesine ve Termometre'ye dahil edilmiyor."
        : a.kind === "vehicle"
          ? "Manuel olarak Termometre dışı tutuluyor (ev için satılması düşünülmüyor)."
          : "Termometre dışı tutuluyor.";
    rows.push({
      key: `oa:${a.id}`,
      name: a.name,
      kind_label: kindLabel[a.kind] ?? a.kind,
      owner_label: a.owner !== "self" ? ownerLabel[a.owner] ?? a.owner : undefined,
      value_tl: safeNum(a.estimated_value_tl),
      is_stale: !!a.is_stale,
      reason,
    });
  }

  // Hiçbir ayrı varlık yoksa ve total_assets değeri Termometre toplamıyla
  // aynıysa kart anlamsız — render etme.
  if (rows.length === 0 && Math.abs(totalAssets - termometerToplam) < 1) {
    return null;
  }

  return (
    <section className="px-4 mt-4" aria-label="Diğer varlıklar">
      <h3 className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold px-1 mb-2">
        Ayrı Tutulan Varlıklar
      </h3>
      <div className="rounded-xl bg-ink-800 border border-ink-700 p-3 space-y-2">
        {rows.length > 0 && (
          <div className="space-y-1.5">
            {rows.map((r) => (
              <div
                key={r.key}
                className="flex items-baseline justify-between gap-2 text-sm"
                title={r.reason}
              >
                <div className="min-w-0 flex items-baseline gap-2">
                  <span className="text-ink-200 truncate">{r.name}</span>
                  <span className="text-[10px] text-ink-500 truncate">
                    {r.kind_label}
                    {r.owner_label && (
                      <span className="ml-1">· {r.owner_label}</span>
                    )}
                  </span>
                  {r.is_stale && (
                    <span
                      className="text-[9px] uppercase tracking-wider text-warn-400 font-semibold"
                      title="Endeks gecikmeli"
                    >
                      gec
                    </span>
                  )}
                </div>
                <span className="fon-dili text-ink-300 whitespace-nowrap">
                  {fmtTLCompact(r.value_tl)}
                </span>
              </div>
            ))}
            <p
              className="text-[10px] text-ink-500 leading-snug pt-1"
              title="Properties + BES + Termometre dışı tutulan diğer varlıklar. Likit oldukları gün Termometre'ye otomatik girer."
            >
              Bu kalemler likit değil veya bilinçli olarak hedef dışı tutuluyor —
              Termometre payına girmiyor.
              {propertyAnyStale && (
                <span className="text-warn-400 ml-1">
                  · Bir gayrimenkul endeksi gecikmeli.
                </span>
              )}
            </p>
          </div>
        )}

        <div className="border-t border-ink-700 pt-2.5">
          <div className="flex items-baseline justify-between">
            <span className="text-ink-100 text-sm font-medium">
              Toplam Varlık
            </span>
            <span className="fon-dili text-ink-100 text-sm font-semibold">
              {fmtTLCompact(totalAssets)}
            </span>
          </div>
          {totalAssets > termometerToplam && (
            <p
              className="text-[10px] text-ink-500 mt-0.5"
              title="Termometre payı (likit) = Toplam Varlık − ayrı tutulan kalemler"
            >
              Likit {fmtTLCompact(termometerToplam)} · fark{" "}
              {fmtTLCompact(totalAssets - termometerToplam)} ayrı tutuluyor
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
